import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

export const resolveInlineEditTarget = ({
	editorView,
	targetId,
}: {
	editorView: EditorView;
	targetId: string | undefined;
}): HTMLElement | null => {
	if (!targetId) {
		return null;
	}

	let targetPos: number | undefined;
	editorView.state.doc.descendants((node, pos) => {
		if (node.attrs.localId === targetId || node.attrs.id === targetId) {
			targetPos = pos;
			return false;
		}
		return true;
	});

	const targetDom = targetPos !== undefined ? editorView.nodeDOM(targetPos) : null;
	return targetDom instanceof HTMLElement ? targetDom : (targetDom?.parentElement ?? null);
};

export const isInlineEditTargetInView = ({
	editorView,
	targetId,
}: {
	editorView: EditorView;
	targetId: string | undefined;
}): boolean | undefined => {
	const target = resolveInlineEditTarget({ editorView, targetId });
	if (!target) {
		return;
	}

	try {
		const editorDocument = editorView.dom.ownerDocument;
		const editorWindow = editorDocument.defaultView;
		const root = findOverflowScrollParent(editorView.dom);
		const targetRect = target.getBoundingClientRect();
		const windowRect = {
			bottom: editorWindow?.innerHeight ?? editorDocument.documentElement.clientHeight,
			left: 0,
			right: editorWindow?.innerWidth ?? editorDocument.documentElement.clientWidth,
			top: 0,
		};
		const measuredRootRect =
			root && root !== editorDocument.body ? root.getBoundingClientRect() : windowRect;
		const visibleRootRect = {
			bottom: Math.min(measuredRootRect.bottom, windowRect.bottom),
			left: Math.max(measuredRootRect.left, windowRect.left),
			right: Math.min(measuredRootRect.right, windowRect.right),
			top: Math.max(measuredRootRect.top, windowRect.top),
		};

		return (
			targetRect.bottom > visibleRootRect.top &&
			targetRect.right > visibleRootRect.left &&
			targetRect.top < visibleRootRect.bottom &&
			targetRect.left < visibleRootRect.right
		);
	} catch {
		return;
	}
};
